/**
 * 建立活動入口共用版型（Group / Square 一致）
 * 標題、說明、「+ 新增活動」、「從菜單建立」（選配）、「已儲存的活動」區塊（選配）
 */

import type { ReactNode } from 'react';
import { ImageIcon, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  MobileNavbar,
  MobileNavbarBackButton,
} from '@/components/ui/mobile-navbar';
import { MobileCard, MobileCardTitle } from '@/components/ui/mobile-card';

export interface CreateActivityEntryViewProps {
  /** 返回按鈕點擊（contentOnly 時由父層導航列處理，可省略） */
  onBack: () => void;
  /** 點擊「新增活動」 */
  onNewActivity: () => void;
  /** 是否顯示「從菜單建立」按鈕（Group / Square 皆顯示兩種方式，預設 true） */
  showFromMenu?: boolean;
  /** 點擊「從菜單建立」 */
  onFromMenu?: () => void;
  /** 已儲存的活動區塊（Group 傳 draft 列表，Square 可傳 null 或空） */
  savedListSection?: ReactNode;
  /** 僅渲染內容區（不含導航列與外層），供 HoldPage 內嵌使用 */
  contentOnly?: boolean;
}

/**
 * 建立活動入口：與 Group / Square 共用同一套第一屏
 */
export function CreateActivityEntryView({
  onBack,
  onNewActivity,
  showFromMenu = true,
  onFromMenu,
  savedListSection,
  contentOnly = false,
}: CreateActivityEntryViewProps) {
  const entryContent = (
    <div className="p-4 space-y-4">
      <MobileCard strong inset>
        <MobileCardTitle>
          建立購買清單或活動，分享給其他人一起參與
        </MobileCardTitle>
        <div className="mt-4 space-y-2">
          <Button size="lg" onClick={onNewActivity} className="w-full">
            <Plus className="mr-2 h-5 w-5" aria-hidden />
            新增活動
          </Button>
          {showFromMenu && onFromMenu && (
            <Button
              size="lg"
              variant="outline"
              onClick={onFromMenu}
              className="w-full"
            >
              <ImageIcon className="mr-2 h-5 w-5" aria-hidden />
              從菜單建立
            </Button>
          )}
        </div>
      </MobileCard>

      {savedListSection}
    </div>
  );

  if (contentOnly) {
    return <>{entryContent}</>;
  }

  return (
    <div className="min-h-screen bg-gray-50 safe-area-container">
      <MobileNavbar
        title="建立活動"
        left={<MobileNavbarBackButton onClick={onBack} />}
      />
      {entryContent}
    </div>
  );
}
